import { useEffect } from 'react';
import { useMeQuery } from '@frameio/graphql-client/generated';
import { intl } from '@frameio/i18n';
import {
  useConsentOverride,
  useIsSegmentLoaded,
  useIsUserIdentified,
} from '@frameio/segment/contexts';
import { getOneTrustConsent } from '@frameio/segment/utils/getOneTrustConsent';
import { processQueue } from '@frameio/segment-ot';
import { getIdentifiedUserId } from '@frameio/utils/auth/shareAuth';
import { getDevFlag } from '../../dev-flags';

export type IdentifyArgs = {
  callback?: () => void;
  isSegmentDebuggerEnabled?: boolean;
  options?: Record<string, any>;
  userId: string | null;
  traits?: Record<string, any>;
  isEdu?: boolean;
  consentOverride?: string[] | null;
};

type SegmentOpts = {
  integrations?: any;
  anonymousId?: string;
  context?: object;
  consent?: undefined | { categoryPreferences: Record<string, any> };
};

type UseIdentifyArgs = Omit<
  IdentifyArgs,
  'isSegmentDebuggerEnabled' | 'userId'
>;

/**
 * Generic `identify` function that fires Segment `identify` call. Takes an
 * object containing the userId, traits, options, isSegmentDebuggerEnabled and
 * an optional callback in order to fire Segment's `identify` call.
 * @param {Object} data - An object containing the `userId`, `traits`,
 * `options`, `isSegmentDebuggerEnabled` and `callback`.
 *
 * Example use:
 * ```
 * identify({ userId, options, isSegmentDebuggerEnabled });
 * ```
 *
 * Prefer `useIdentify` (below) as the default entry point.
 */
export function identify({
  callback,
  isSegmentDebuggerEnabled = false,
  options,
  userId,
  traits = {},
  isEdu,
  consentOverride,
}: IdentifyArgs) {
  if (!window.analytics) return;

  // add GDPR consent to the identify options
  const consent = getOneTrustConsent(isEdu, consentOverride);

  window.analytics.identify(
    userId as any,
    traits,
    { ...options, ...consent } as SegmentOpts,
    () => {
      callback?.();

      if (isSegmentDebuggerEnabled) {
        // eslint-disable-next-line no-console
        console.log(
          '%c%s - %c%s %o \n',
          'padding: 1px; font-weight: bold; color: #52bd95;',
          'Segment Identify Event',
          'padding: 1px; font-weight: bold; color:#5B53FF',
          userId,
          traits,
          options
        );
      }
    }
  );
}

const isSegmentDebuggerEnabled = getDevFlag('SEGMENT_DEBUGGER');
/**
 * Generic `identify` hook.
 * Takes an object containing the userId, options and a routeType in order
 * to fire our `identify` call.
 * @param {Object} data - An object containing Segment `options`
 *
 * Example use:
 * ```
 * useIdentify({ options })
 * ```
 */
export const useIdentify = ({ options = {} }: UseIdentifyArgs): void => {
  const localeContext = intl();
  const locale = localeContext.locale;
  const { isSegmentLoaded } = useIsSegmentLoaded();
  const { isUserIdentified, setUserIdentified } = useIsUserIdentified();
  const consentOverride = useConsentOverride();

  const { data: user } = useMeQuery({
    skip: !isSegmentLoaded,
    context: {
      // Avoid redirecting to login on this call (e.g. shares)
      shouldRedirectToLoginIfUnauthenticated: false,
      bypassShareAuth: true,
    },
    fetchPolicy: 'cache-first',
  });
  const authenticatedUserId =
    user?.me?.__typename === 'AuthenticatedUser' ? user?.me?.id : null;

  const isEdu =
    user?.me?.__typename === 'AuthenticatedUser'
      ? user?.me?.isEdu || false
      : false;

  useEffect(
    function identifyUser() {
      if (isUserIdentified) return;

      // Wait for Segment to load before identifying
      if (!isSegmentLoaded) return;

      const identifiedUserId = getIdentifiedUserId();

      const identifyCallback = () => {
        setUserIdentified(true);
        processQueue();
      };

      if (authenticatedUserId) {
        identify({
          callback: identifyCallback,
          consentOverride,
          isEdu,
          isSegmentDebuggerEnabled,
          options,
          traits: { language: locale },
          userId: authenticatedUserId,
        });
      } else if (identifiedUserId) {
        // IdentifiedUser: add traits so Segment can associate email with the identified user and merge sessions i.e. Known User
        identify({
          callback: identifyCallback,
          consentOverride,
          isEdu,
          isSegmentDebuggerEnabled,
          options,
          userId: null,
          traits: {
            email: user?.me?.email,
            name: user?.me?.name,
            identified_user_id: identifiedUserId,
            language: locale,
          },
        });
      } else {
        // Anonymous user
        identify({
          callback: identifyCallback,
          consentOverride,
          isEdu,
          isSegmentDebuggerEnabled,
          options,
          traits: { language: locale },
          userId: null,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      authenticatedUserId,
      consentOverride,
      isSegmentDebuggerEnabled,
      isSegmentLoaded,
      isUserIdentified,
      locale,
      user?.me?.email,
      user?.me?.name,
    ]
  );
};
